import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin } from 'rxjs';
import { JobRequestReportDto } from '../../models/job-request-report-dto.interface';
import { OverviewRowDto } from '../../models/job-request-overview-dto.interface';
import {
  ContentFieldDto,
  DesignIterationDto,
  DesignTestInputDto,
  FormulationComponentDto,
  JobRequestIterationsDto
} from '../../models/job-request-iterations-dto.interface';
import { DesignService } from '../../services/design.service';
import { JobRequestsService } from '../../services/job-requests.service';
import { calculateNafChemistryTitration } from '../../shared/utils/naf-chemistry-calculations';
import { VerticalTestComponentsModule } from './vertical-test-components.module';
import { reportPageEnds, ReportBlock } from './report-pagination';

interface ColumnTestRow {
  key: string;
  typeName: string;
  title: string;
  showHeading: boolean;
  cells: Array<DesignTestInputDto | null>;
}

interface ColumnTestGroup {
  key: string;
  title: string;
  rows: ColumnTestRow[];
}

interface ElectricalStabilityParsedReading {
  id: string | null;
  temperature: number | null;
  voltages: number[];
}

interface SalinityComputedValues {
  nafMudAlkalinity: number;
  wholeMudChloridesVolume: number | null;
  wholeMudCalciumVolume: number | null;
  limeContent: number;
}

interface WbmChemistryComputedValues {
  wholeMudAlkalinity: number | null;
  filtrateAlkalinityPf: number | null;
  filtrateAlkalinityMf: number | null;
  filtrateChlorideContent: number | null;
  filtrateTotalHardness: number | null;
  filtrateCalciumContent: number | null;
  filtrateMagnesiumContent: number | null;
  wholeMudChlorideContent: number | null;
  wholeMudCalciumContent: number | null;
}

interface WbmTitrationComputedValues {
  totalChlorides: number | null;
}

interface PsdComputedValues {
  valueForPercentile10: number | null;
  valueForPercentile50: number | null;
  valueForPercentile90: number | null;
  valueForSize6: number | null;
  valueForSize44: number | null;
  valueForSize74: number | null;
}

interface StaticAgingComputedValues {
  freeFluidVolumePercent: number | null;
  shearStrength: number | null;
  sagFactor: number | null;
  sagIndex: number | null;
}

interface TestBucket {
  key: string;
  typeName: string;
  title: string;
  order: number;
  testsByDesign: Map<number, DesignTestInputDto[]>;
}

interface GroupBucket {
  key: string;
  title: string;
  tests: Map<string, TestBucket>;
}

@Component({
  selector: 'app-job-request-column-report',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    VerticalTestComponentsModule
  ],
  templateUrl: './job-request-column-report.component.html',
  styleUrl: './job-request-column-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestColumnReportComponent implements OnInit, OnDestroy {
  private static readonly PDF_PAGE_MARGIN_INCHES = 0.35;
  private static readonly TEST_ORDER = [
    'density',
    'pycnometer',
    'rheology',
    'electricalstability',
    'hthp',
    'hphtfluidloss',
    'hthpfluidloss',
    'apifluidloss',
    'ppt',
    'retort',
    'salinity',
    'nafchemistry',
    'wbmsalinity',
    'wbmchemistry',
    's3s4chlorides',
    'wbmchlorides',
    'apisalinity',
    'psd',
    'hphtrheology',
    'hthprheology',
    'staticaging',
    'other'
  ];

  @ViewChild('reportDocument') private reportDocument?: ElementRef<HTMLElement>;

  protected readonly report = signal<JobRequestReportDto | null>(null);
  protected readonly iterations = signal<JobRequestIterationsDto | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly showFormulation = signal(false);
  protected readonly isGeneratingPdf = signal(false);
  protected readonly pdfError = signal(false);
  protected readonly pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  protected readonly pdfDownloadUrl = signal<string | null>(null);
  protected readonly pdfFilename = signal('column-report.pdf');
  protected readonly designs = computed(() => this.iterations()?.designs ?? []);
  protected readonly testGroups = computed(() => this.buildColumnGroups(this.designs()));

  private readonly route = inject(ActivatedRoute);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    const requestId = Number(this.route.snapshot.paramMap.get('id'));
    this.showFormulation.set(
      this.route.snapshot.queryParamMap.get('showFormulation')?.toLowerCase() === 'true'
    );

    forkJoin({
      report: this.jobRequestsService.getReport(requestId),
      iterations: this.designService.getIterations(requestId)
    }).subscribe({
      next: ({ report, iterations }) => {
        this.report.set(report);
        this.iterations.set(this.filterSelectedDesigns(iterations));
        this.isLoading.set(false);
        this.pdfFilename.set(this.createFilename(report));
        this.queuePdfGeneration();
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokePdfUrl();
  }

  protected regeneratePdf(): void {
    this.revokePdfUrl();
    this.pdfPreviewUrl.set(null);
    this.pdfDownloadUrl.set(null);
    this.queuePdfGeneration();
  }

  protected openPdf(): void {
    const url = this.pdfDownloadUrl();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  private queuePdfGeneration(): void {
    this.pdfError.set(false);
    this.isGeneratingPdf.set(true);
    requestAnimationFrame(() => requestAnimationFrame(() => void this.generatePdf()));
  }

  private async generatePdf(): Promise<void> {
    const source = this.reportDocument?.nativeElement;
    if (!source) {
      this.isGeneratingPdf.set(false);
      this.pdfError.set(true);
      return;
    }

    try {
      source.classList.add('pdf-capture');
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      await document.fonts.ready;

      const fontScale = 0.8;
      const typographyProperties = [
        'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing'
      ];
      const typography = [source, ...Array.from(source.querySelectorAll<HTMLElement>('*'))]
        .map(element => {
          const style = getComputedStyle(element);
          return typographyProperties.map(property => style.getPropertyValue(property));
        });
      const applyTypography = async (clonedDocument: Document): Promise<void> => {
        const clonedSource = clonedDocument.querySelector<HTMLElement>('.html2pdf__container .report-document');
        if (!clonedSource) throw new Error('PDF report clone is missing.');
        [clonedSource, ...Array.from(clonedSource.querySelectorAll<HTMLElement>('*'))]
          .filter(element => !element.closest('html2canvaspseudoelement'))
          .forEach((element, index) => {
            typography[index]?.forEach((value, propertyIndex) => {
              const property = typographyProperties[propertyIndex];
              const scaledValue = (property === 'font-size' || property === 'line-height') && value.endsWith('px')
                ? `${parseFloat(value) * fontScale}px`
                : value;
              element.style.setProperty(property, scaledValue, 'important');
            });
          });
        await clonedDocument.fonts.ready;
      };

      const { default: html2pdf } = await import('html2pdf.js');
      const margin = JobRequestColumnReportComponent.PDF_PAGE_MARGIN_INCHES;
      const contentWidth = 8.5 - 2 * margin;
      const contentHeight = 11 - 2 * margin;
      let captureWidth = 0;
      let blocks: ReportBlock[] = [];
      const pdfOptions = {
        margin,
        filename: this.pdfFilename(),
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          backgroundColor: '#ffffff',
          logging: false,
          scale: 2,
          useCORS: true,
          windowWidth: 1280,
          onclone: async (clonedDocument: Document) => {
            await applyTypography(clonedDocument);
            const container = clonedDocument.querySelector<HTMLElement>('.html2pdf__container');
            if (!container) throw new Error('PDF capture container is missing.');
            const bounds = container.getBoundingClientRect();
            captureWidth = bounds.width;
            blocks = Array.from(container.querySelectorAll<HTMLElement>('.avoid-break, tr'))
              .map(element => {
                const rect = element.getBoundingClientRect();
                return { top: rect.top - bounds.top, bottom: rect.bottom - bounds.top };
              });
            for (const group of Array.from(container.querySelectorAll<HTMLElement>('.test-group'))) {
              const heading = group.querySelector('h2');
              const firstRow = group.querySelector('.test-row');
              if (heading && firstRow) {
                blocks.push({
                  top: heading.getBoundingClientRect().top - bounds.top,
                  bottom: firstRow.getBoundingClientRect().bottom - bounds.top
                });
              }
            }
          }
        },
        pagebreak: {
          mode: []
        },
        jsPDF: {
          unit: 'in',
          format: [8.5, 11],
          orientation: 'portrait'
        }
      } as any;
      const canvas = await html2pdf()
        .set(pdfOptions)
        .from(source)
        .toCanvas()
        .get('canvas') as HTMLCanvasElement;
      if (captureWidth <= 0) throw new Error('PDF capture width is invalid.');

      const scale = canvas.width / captureWidth;
      const pageHeight = Math.floor(canvas.width * contentHeight / contentWidth);
      const imageHeight = pageHeight * contentWidth / canvas.width;
      const pageEnds = reportPageEnds(canvas.height, pageHeight, blocks.map(block => ({
        top: Math.floor(block.top * scale),
        bottom: Math.ceil(block.bottom * scale)
      })));
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeight;
      const context = pageCanvas.getContext('2d');
      if (!context) throw new Error('PDF page canvas is unavailable.');

      let pageStart = 0;
      let pdf: any;
      for (const pageEnd of pageEnds) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(canvas, 0, pageStart, canvas.width, pageEnd - pageStart,
          0, 0, canvas.width, pageEnd - pageStart);
        if (!pdf) {
          pdf = await html2pdf().set(pdfOptions).from(pageCanvas).toPdf().get('pdf');
        } else {
          pdf.addPage();
          pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.98), 'JPEG', margin, margin,
            contentWidth, imageHeight);
        }
        pageStart = pageEnd;
      }
      const blob = pdf.output('blob') as Blob;

      this.revokePdfUrl();
      const objectUrl = URL.createObjectURL(blob);
      this.pdfDownloadUrl.set(objectUrl);
      this.pdfPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl));
    } catch {
      this.pdfError.set(true);
    } finally {
      source.classList.remove('pdf-capture');
      this.isGeneratingPdf.set(false);
    }
  }

  private revokePdfUrl(): void {
    const currentUrl = this.pdfDownloadUrl();
    if (currentUrl) URL.revokeObjectURL(currentUrl);
  }

  private createFilename(report: JobRequestReportDto): string {
    const baseName = `${report.name ?? `request-${report.id}`}-column-report`
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
    return `${baseName || 'column-report'}.pdf`;
  }

  protected componentName(component: FormulationComponentDto): string {
    return component.name
      || component.materialName
      || component.chemicalName
      || `Component ${component.id}`;
  }

  protected designWeight(design: DesignIterationDto, component: FormulationComponentDto): number | null {
    return design.materials.find(material => material.formulationComponentId === component.id)?.weight ?? null;
  }

  protected isType(typeName: string, ...types: string[]): boolean {
    return types.some(type => typeName.includes(type));
  }

  protected reportTestRow(test: DesignTestInputDto): OverviewRowDto | undefined {
    return this.report()?.overviewGroups
      .flatMap(group => group.overviews)
      .flatMap(overview => overview.rows)
      .find(row => row.testInputId === test.id);
  }

  protected verticalTest(test: DesignTestInputDto, design: DesignIterationDto): Record<string, unknown> {
    const values = this.testValues(test);
    const specificGravity = this.numberValue(values, ['specificgravity', 'densitygml', 'densitygrampermilliliter']);
    const directDensity = this.numberValue(values, ['densitylbgal', 'densitylbpergal', 'densityppg', 'density']);
    const density = directDensity !== null && directDensity > 3
      ? directDensity
      : specificGravity !== null
        ? specificGravity * 8.345
        : directDensity !== null
          ? directDensity * 8.345
          : null;
    const isPycnometer = this.isType(
      this.normalize(test.testTypeAlias || test.testTypeName || test.title || ''),
      'pycnometer'
    );
       
    const averageSg = isPycnometer ? this.pycnometerAverageSg(values) : null;
    const averageDensity = averageSg !== null ? averageSg * 8.345 : null;
    const mudSpecificGravity = this.retortMudSpecificGravity(design);
    const cellMudWeight = this.numberValue(values, ['cellmudweight', 'cellwithmudweight']);
    const cellTareWeight = this.numberValue(values, ['celltareweight', 'cellweight']);
    const cellAfterWeight = this.numberValue(values, ['cellafterweight']);
    const totalVolume = this.numberValue(values, ['totalvolume', 'samplevolumeml']);
    const waterVolume = this.numberValue(values, ['watervolume']);
    const sampleWeight = this.subtract(cellMudWeight, cellTareWeight);
    const solidsWeight = this.subtract(cellAfterWeight, cellTareWeight);
    const nafVolume = totalVolume !== null && waterVolume !== null
      ? Math.max(totalVolume - waterVolume, 0)
      : null;
    const mudVolume = sampleWeight !== null && mudSpecificGravity !== null && mudSpecificGravity > 0
      ? sampleWeight / mudSpecificGravity
      : null;
    const nafOfTotal = this.numberValue(values, ['retortnafoftotal', 'nafoftotal', 'nafpercent', 'nafvolpercent'])
      ?? this.percent(nafVolume, mudVolume);
    const waterOfTotal = this.numberValue(values, ['retortwateroftotal', 'wateroftotal', 'waterpercent', 'watervolpercent'])
      ?? this.percent(waterVolume, mudVolume);
    const liquidVolume = this.add(nafVolume, waterVolume);
    const baseFluidSpecificGravity = this.report()?.baseFluidSpecificGravity ?? 0;
    const salinityComputed = this.salinityComputedValues(values);
    const wbmChemistryComputed = this.wbmChemistryComputedValues(values);
    const wbmTitrationComputed = this.wbmTitrationComputedValues(values, test, design);
    const psdComputed = this.psdComputedValues(values);
    const staticAgingComputed = this.isType(
      this.normalize(test.testTypeAlias || test.testTypeName || test.title || ''),
      'staticaging'
    ) ? this.staticAgingComputedValues(values, design) : null;
    return {
      title: test.title,
      specificGravity: specificGravity ?? (density !== null ? density / 8.345 : null),
      density,
      averageSg: isPycnometer ? averageSg : null,
      averageDensity: isPycnometer ? averageDensity : null,
      results: this.rheologyRows(test),
      results_fann35: this.rheologyReferenceRows(design, test),
      discGrade: this.value(values, ['discgrade']),
      temperature: this.numberValue(values, ['temperature', 'testtemperature']),
      pressure: this.numberValue(values, ['pressure', 'testpressure']),
      duration: this.value(values, ['duration', 'agingduration']),
      spurtVolume: this.numberValue(values, ['spurtvolume']),
      pptValue: this.numberValue(values, ['pptvalue', 'filtratevolume', 'filtrate30min']),
      cakeThickness: this.numberValue(values, ['cakethickness']),
      filtrateVolume: this.numberValue(values, ['filtratevolume', 'filtrate30min']),
      hphtFiltrate: this.numberValue(values, ['hphtfiltrate', 'hthpfiltrate', 'filtrate30min']),
      waterVolume,
      averageFor120: this.electricalStabilityAverage(values, 120, test),
      averageFor150: this.electricalStabilityAverage(values, 150, test),
      solidsOfTotal: this.numberValue(values, ['retortsolidsvolumepercent', 'solidsvolumepercent', 'solidspercent'])
        ?? (nafOfTotal !== null && waterOfTotal !== null ? 100 - nafOfTotal - waterOfTotal : null),
      nafOfTotal,
      nafOfLiquid: this.percent(nafVolume, liquidVolume),
      waterOfLiquid: this.percent(waterVolume, liquidVolume),
      waterOfTotal,
      errorMargin: this.numberValue(values, ['retorterrormargin', 'retortqa', 'qapercent', 'qa'])
        ?? (sampleWeight !== null && sampleWeight > 0 && solidsWeight !== null
          && waterVolume !== null && nafVolume !== null
          ? 100 * (solidsWeight + waterVolume + nafVolume * baseFluidSpecificGravity) / sampleWeight
          : null),
      valueForPercentile10: this.numberValue(values, ['percentile10_size', 'd10', 'percentile10size'])
        ?? psdComputed.valueForPercentile10,
      valueForPercentile50: this.numberValue(values, ['percentile50_size', 'd50', 'percentile50size'])
        ?? psdComputed.valueForPercentile50,
      valueForPercentile90: this.numberValue(values, ['percentile90_size', 'd90', 'percentile90size'])
        ?? psdComputed.valueForPercentile90,
      valueForSize6: this.numberValue(values, ['size6_percentile', 'diameter6percentile', 'size6percentile'])
        ?? psdComputed.valueForSize6,
      valueForSize44: this.numberValue(values, ['size44_percentile', 'diameter44percentile', 'size44percentile'])
        ?? psdComputed.valueForSize44,
      valueForSize74: this.numberValue(values, ['size74_percentile', 'diameter74percentile', 'size74percentile'])
        ?? psdComputed.valueForSize74,
      totalChlorides: this.numberValue(values, [
        'wbmTitration_totalChlorides',
        'totalChloridesGml',
        'totalchlorides',
        'chloridecontent'
      ]) ?? wbmTitrationComputed.totalChlorides,
      nafMudAlkalinity: this.numberValue(values, [
        'salinity_nafMudAlkalinity',
        'nafmudalkalinity',
        'alkalinity'
      ]) ?? salinityComputed.nafMudAlkalinity,
      wholeMudChloridesVolume: this.numberValue(values, [
        'salinity_wholeMudChlorides',
        'wholemudchloridesvolume',
        'wholemudchlorides',
        'chlorides'
      ]) ?? salinityComputed.wholeMudChloridesVolume,
      wholeMudCalciumVolume: this.numberValue(values, [
        'salinity_wholeMudCalcium',
        'wholemudcalciumvolume',
        'wholemudcalcium',
        'calcium'
      ]) ?? salinityComputed.wholeMudCalciumVolume,
      limeContent: this.numberValue(values, [
        'salinity_limeContent',
        'limecontent',
        'excesslime'
      ]) ?? salinityComputed.limeContent,
      sandContentPercentByVolume: this.numberValue(values, ['sandcontentpercentbyvolume', 'sandcontent']),
      methyleneBlueCapacity: this.numberValue(values, ['methylenebluecapacity']),
      ph: this.numberValue(values, ['ph']),
      wholeMudAlkalinity: this.numberValue(values, [
        'wbmChemistry_mudAlkalinity',
        'mudalkalinity',
        'wholemudalkalinity'
      ]) ?? wbmChemistryComputed.wholeMudAlkalinity,
      filtrateAlkalinityPf: this.numberValue(values, [
        'wbmChemistry_filtrateAlkalinityPf',
        'filtratealkalinitypf'
      ]) ?? wbmChemistryComputed.filtrateAlkalinityPf,
      filtrateAlkalinityMf: this.numberValue(values, [
        'wbmChemistry_filtrateAlkalinityMf',
        'filtratealkalinitymf'
      ]) ?? wbmChemistryComputed.filtrateAlkalinityMf,
      filtrateChlorideContent: this.numberValue(values, [
        'wbmChemistry_filtrateChlorides',
        'filtratechlorides',
        'filtratechloridecontent'
      ]) ?? wbmChemistryComputed.filtrateChlorideContent,
      filtrateTotalHardness: this.numberValue(values, [
        'wbmChemistry_filtrateTotalHardness',
        'filtratetotalhardness'
      ]) ?? wbmChemistryComputed.filtrateTotalHardness,
      filtrateCalciumContent: this.numberValue(values, [
        'wbmChemistry_filtrateCalcium',
        'filtratecalcium',
        'filtratecalciumcontent'
      ]) ?? wbmChemistryComputed.filtrateCalciumContent,
      filtrateMagnesiumContent: this.numberValue(values, [
        'wbmChemistry_filtrateMagnesium',
        'filtratemagnesium',
        'filtratemagnesiumcontent'
      ]) ?? wbmChemistryComputed.filtrateMagnesiumContent,
      wholeMudChlorideContent: this.numberValue(values, [
        'wbmChemistry_wholeMudChloride',
        'wholemudchloride',
        'wholemudchloridecontent'
      ]) ?? wbmChemistryComputed.wholeMudChlorideContent,
      wholeMudCalciumContent: this.numberValue(values, [
        'wbmChemistry_wholeMudCalcium',
        'wholemudcalcium',
        'wholemudcalciumcontent'
      ]) ?? wbmChemistryComputed.wholeMudCalciumContent,
      correctedSolidsVolumePercent: this.numberValue(values, ['correctedsolidsvolumepercent']),
      totalCalciumChlorideConcentrationInLnPerBbl: this.numberValue(values, ['totalcalciumchlorideconcentrationinlnperbbl', 'totalcalciumchlorideconcentrationinlbperbbl']),
      lowGravitySolidsConcentrationInLnPerBbl: this.numberValue(values, ['lowgravitysolidsconcentrationinlnperbbl', 'lowgravitysolidsconcentrationinlbperbbl']),
      lowGravitySolidsByVolumePercent: this.numberValue(values, ['lowgravitysolidsbyvolumepercent']),
      totalSodiumChlorideConcentrationInLnPerBbl: this.numberValue(values, ['totalsodiumchlorideconcentrationinlnperbbl', 'totalsodiumchlorideconcentrationinlbperbbl']),
      weighingAgentConcentrationInLnPerBbl: this.numberValue(values, ['weighingagentconcentrationinlnperbbl', 'weighingagentconcentrationinlbperbbl']),
      weighingAgentByVolumePercent: this.numberValue(values, ['weighingagentbyvolumepercent']),
      solubleSodiumChlorideConcentrationInLnPerBbl: this.numberValue(values, ['solublesodiumchlorideconcentrationinlnperbbl', 'solublesodiumchlorideconcentrationinlbperbbl']),
      insolubleSodiumChlorideConcentrationInLnPerBbl: this.numberValue(values, ['insolublesodiumchlorideconcentrationinlnperbbl', 'insolublesodiumchlorideconcentrationinlbperbbl']),
      averageSolidsDensity: this.numberValue(values, ['averagesolidsdensity']),
      waterPhaseSalinity: this.numberValue(values, ['waterphasesalinity']),
      output: {
        freeFluidVolumePercent: staticAgingComputed?.freeFluidVolumePercent
          ?? this.numberValue(values, ['freefluidvolumepercent']),
        shearStrength: staticAgingComputed?.shearStrength
          ?? this.numberValue(values, ['shearstrength']),
        sagFactor: staticAgingComputed?.sagFactor
          ?? this.numberValue(values, ['sagfactor']),
        sagIndex: staticAgingComputed?.sagIndex
          ?? this.numberValue(values, ['sagindex'])
      },
      notes: this.testFields(test).map(field => ({ text: `${field.label}: ${field.value}` }))
    };
  }

  protected rheologyReferenceTest(
    design: DesignIterationDto,
    test: DesignTestInputDto
  ): DesignTestInputDto | null {
    const associatedGroup = design.testGroups.find(group =>
      group.testInputs.some(candidate => candidate.id === test.id));
    const candidates = [
      ...(associatedGroup?.testInputs ?? []),
      ...design.testGroups
        .filter(group => group !== associatedGroup)
        .flatMap(group => group.testInputs)
    ];

    return candidates.find(candidate =>
      candidate.id !== test.id && this.isStandardRheologyTest(candidate)) ?? null;
  }

  protected retortMudSpecificGravity(design: DesignIterationDto): number | null {
    const densityOutputs = design.testGroups
      .flatMap(group => group.testInputs)
      .filter(test => this.isDensityTest(test))
      .flatMap(test => test.testOutputs);

    for (const output of densityOutputs) {
      const field = output.parsedValues.find(value =>
        ['specificgravity', 'densitygml'].includes(this.normalize(value.label)));
      const specificGravity = Number(field?.value);
      if (Number.isFinite(specificGravity) && specificGravity > 0) return specificGravity;
    }

    const baselineDensityPpg = this.iterations()?.baselineDensityPpg ?? this.report()?.originalDensity;
    return baselineDensityPpg !== null
      && baselineDensityPpg !== undefined
      && baselineDensityPpg > 0
      ? baselineDensityPpg / 8.345
      : null;
  }

  private filterSelectedDesigns(iterations: JobRequestIterationsDto): JobRequestIterationsDto {
    const selectedIds = new Set(
      this.route.snapshot.queryParamMap.getAll('designid')
        .map(value => Number(value))
        .filter(Number.isFinite)
    );

    if (selectedIds.size === 0) return iterations;

    return {
      ...iterations,
      designs: iterations.designs.filter(design => selectedIds.has(design.id))
    };
  }

  private buildColumnGroups(designs: DesignIterationDto[]): ColumnTestGroup[] {
    const groups = new Map<string, GroupBucket>();

    for (const design of designs) {
      for (const group of design.testGroups) {
        const groupTitle = group.name?.trim() || 'Test Results';
        const groupKey = this.normalize(groupTitle);
        let groupBucket = groups.get(groupKey);

        if (!groupBucket) {
          groupBucket = { key: groupKey, title: groupTitle, tests: new Map() };
          groups.set(groupKey, groupBucket);
        }

        for (const test of group.testInputs) {
          const testTitle = test.title?.trim() || test.testTypeName?.trim() || 'Other';
          const typeKey = this.normalize(test.testTypeAlias || test.testTypeName || String(test.testTypeId));
          const testKey = `${typeKey}:${this.normalize(testTitle)}`;
          let testBucket = groupBucket.tests.get(testKey);

          if (!testBucket) {
            testBucket = {
              key: testKey,
              typeName: typeKey,
              title: testTitle,
              order: this.testOrder(typeKey),
              testsByDesign: new Map()
            };
            groupBucket.tests.set(testKey, testBucket);
          }

          const designTests = testBucket.testsByDesign.get(design.id) ?? [];
          designTests.push(test);
          testBucket.testsByDesign.set(design.id, designTests);
        }
      }
    }

    return Array.from(groups.values()).map(group => ({
      key: group.key,
      title: group.title,
      rows: Array.from(group.tests.values())
        .sort((left, right) => left.order - right.order || left.title.localeCompare(right.title))
        .flatMap(test => this.buildTestRows(test, designs))
    }));
  }

  private buildTestRows(test: TestBucket, designs: DesignIterationDto[]): ColumnTestRow[] {
    const rowCount = Math.max(
      1,
      ...designs.map(design => test.testsByDesign.get(design.id)?.length ?? 0)
    );

    return Array.from({ length: rowCount }, (_, index) => {
      const cells = designs.map(design => test.testsByDesign.get(design.id)?.[index] ?? null);

      return {
        key: `${test.key}:${index}`,
        typeName: test.typeName,
        title: test.title,
        showHeading: index === 0,
        cells
      };
    });
  }

  private testOrder(typeKey: string): number {
    const index = JobRequestColumnReportComponent.TEST_ORDER.findIndex(type => typeKey.includes(type));
    return index === -1 ? JobRequestColumnReportComponent.TEST_ORDER.length : index;
  }

  private isDensityTest(test: DesignTestInputDto): boolean {
    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(value => this.normalize(value ?? ''))
      .some(value => value === 'density' || value === 'densitytest');
  }

  private isStandardRheologyTest(test: DesignTestInputDto): boolean {
    const values = [test.testTypeAlias, test.testTypeName, test.title]
      .map(value => this.normalize(value ?? ''));
    const isHpht = values.some(value =>
      value.includes('hphtrheology') || value.includes('hthprheology') || value.includes('fann77'));
    return !isHpht && (test.testTypeId === 7
      || values.some(value => value === 'rheology' || value === 'rheologytest' || value.includes('rheology')));
  }

  private normalize(value: string): string {
    return value.replace(/[^a-z0-9]+/gi, '').toLowerCase();
  }

  private testValues(test: DesignTestInputDto): Map<string, string[]> {
    const values = new Map<string, string[]>();
    for (const field of this.testFields(test)) {
      this.addValue(values, field.label, field.value);
    }

    const reportRow = this.reportTestRow(test);
    for (const source of [reportRow?.inputParameters, reportRow?.outputParameters]) {
      for (const [key, value] of Object.entries(source ?? {})) {
        if (value?.trim()) this.addValue(values, key, value);
      }
    }

    for (const content of [test.content, test.testOutputs[0]?.content]) {
      if (!content) continue;
      const document = new DOMParser().parseFromString(content, 'application/xml');
      if (document.querySelector('parsererror')) continue;

      for (const element of Array.from(document.getElementsByTagName('*'))) {
        if (element.children.length > 0) continue;
        const value = element.textContent?.trim();
        if (!value) continue;
        const key = element.localName.toLowerCase() === 'parameter'
          ? element.getAttribute('key')
          : element.localName;
        if (key) this.addValue(values, key, value);
      }
    }

    return values;
  }

  private testFields(test: DesignTestInputDto) {
    return [...test.parsedInputValues, ...(test.testOutputs[0]?.parsedValues ?? [])]
      .filter(field => field.label?.trim() && field.value?.trim());
  }

  private addValue(values: Map<string, string[]>, key: string, value: string): void {
    const normalizedKey = this.normalize(key);
    values.set(normalizedKey, [...(values.get(normalizedKey) ?? []), value]);
  }

  private value(values: Map<string, string[]>, names: string[]): string | null {
    for (const name of names) {
      const value = values.get(this.normalize(name))?.[0];
      if (value !== undefined && value !== '') return value;
    }
    return null;
  }

  private numberValue(values: Map<string, string[]>, names: string[], index = 0): number | null {
    for (const name of names) {
      const value = Number(values.get(this.normalize(name))?.[index]);
      if (Number.isFinite(value)) return value;
    }
    return null;
  }

  private rheologyRows(test: DesignTestInputDto): Array<Record<string, unknown>> {
    const inputReadings = this.xmlReadings(
      test.content,
      ['rheologyinputreading', 'rheologyinputreadinglegacy', 'reading']
    );
    const outputReadings = this.xmlReadings(
      test.testOutputs[0]?.content ?? null,
      [
        'rheologyoutputreading',
        'rheologyoutputreadinglegacy',
        'hphtrheologyoutputreading',
        'hphtrheologyoutputreadinglegacy',
        'reading'
      ]
    );
    const rowCount = Math.max(inputReadings.length, outputReadings.length);
    if (rowCount === 0) {
      const values = this.testValues(test);
      const row = this.rheologyRow(values, values);
      return this.hasRheologyValues(row) ? [row] : [];
    }

    const fallbackInput = this.testValues(test);
    return Array.from({ length: rowCount }, (_, index) =>
      this.rheologyRow(
        inputReadings[index] ?? fallbackInput,
        outputReadings[index] ?? new Map(),
        inputReadings[index] ? 0 : index
      ))
      .filter(row => this.hasRheologyValues(row));
  }

  private rheologyReferenceRows(
    design: DesignIterationDto,
    test: DesignTestInputDto
  ): Array<Record<string, unknown>> {
    const typeName = this.normalize(test.testTypeAlias || test.testTypeName || '');
    if (!this.isType(typeName, 'hphtrheology', 'hthprheology', 'fann77')) return [];

    const reference = this.rheologyReferenceTest(design, test);
    return reference ? this.rheologyRows(reference).slice(0, 1) : [];
  }

  private rheologyRow(
    input: Map<string, string[]>,
    output: Map<string, string[]>,
    inputIndex = 0
  ): Record<string, unknown> {
    const rpm600 = this.numberValue(output, ['viscosity600rpm', 'rpm600']);
    const rpm300 = this.numberValue(output, ['viscosity300rpm', 'rpm300']);
    const plasticViscosity = this.numberValue(output, ['plasticviscosity'])
      ?? (rpm600 !== null && rpm300 !== null ? rpm600 - rpm300 : null);
    const yieldPoint = this.numberValue(output, ['yieldpoint'])
      ?? (rpm300 !== null && plasticViscosity !== null ? rpm300 - plasticViscosity : null);

    return {
      temperature: this.numberValue(input, ['temperature'], inputIndex) ?? this.numberValue(output, ['temperature']),
      pressure: this.numberValue(input, ['pressure'], inputIndex) ?? this.numberValue(output, ['pressure']),
      output: {
        viscosity600Rpm: rpm600,
        viscosity300Rpm: rpm300,
        viscosity200Rpm: this.numberValue(output, ['viscosity200rpm', 'rpm200']),
        viscosity100Rpm: this.numberValue(output, ['viscosity100rpm', 'rpm100']),
        viscosity60Rpm: this.numberValue(output, ['viscosity60rpm', 'rpm60']),
        viscosity30Rpm: this.numberValue(output, ['viscosity30rpm', 'rpm30']),
        viscosity20Rpm: this.numberValue(output, ['viscosity20rpm', 'rpm20']),
        viscosity10Rpm: this.numberValue(output, ['viscosity10rpm', 'rpm10']),
        viscosity6Rpm: this.numberValue(output, ['viscosity6rpm', 'rpm6']),
        viscosity3Rpm: this.numberValue(output, ['viscosity3rpm', 'rpm3']),
        plasticViscosity,
        yieldPoint,
        gel10Sec: this.numberValue(output, ['gel10sec', 'gel10s']),
        gel10Min: this.numberValue(output, ['gel10min', 'gel10m']),
        gel30Min: this.numberValue(output, ['gel30min', 'gel30m'])
      }
    };
  }

  private pycnometerAverageSg(values: Map<string, string[]>): number | null {
    const sampleWeight = this.numberValue(values, ['sampleweight']);
    const cellVolume = this.numberValue(values, ['cellvolume']);
    const referenceVolume = this.numberValue(values, ['referencevolume']);
    if (sampleWeight === null || cellVolume === null || referenceVolume === null) return null;
    const readings = [1, 2, 3, 4]
      .map(run => {
        const p1 = this.numberValue(values, [`p1`]);
        const p2 = this.numberValue(values, [`p2`]);
        if (p1 === null || p2 === null || p2 === 0) return null;
       

        const volumeOfPowder = cellVolume - referenceVolume * (p1 / p2 - 1);
        if (!Number.isFinite(volumeOfPowder) || volumeOfPowder <= 0) return null;

        const roundedVolumeOfPowder = Number(volumeOfPowder.toFixed(3));
        if (!Number.isFinite(roundedVolumeOfPowder) || roundedVolumeOfPowder <= 0) return null;

        const specificGravity = Number((sampleWeight / roundedVolumeOfPowder).toFixed(3));
        return Number.isFinite(specificGravity) && specificGravity > 0 ? specificGravity : null;
      })
      .filter((value): value is number => value !== null);

    if (readings.length === 0) return null;

    return Number((readings.reduce((sum, value) => sum + value, 0) / readings.length).toFixed(3));
  }

  private psdComputedValues(values: Map<string, string[]>): PsdComputedValues {
    const percentiles = this.numericValues(values, 'percentile');
    const sizes = this.numericValues(values, 'size');
    const sizeByPercentile = new Map<number, number>();
    const cumulativeByDiameter = new Map<number, number>();
    const supportedPercentiles = new Set([5, 10, 25, 40, 50, 60, 75, 85, 90, 95]);
    const supportedDiameters = new Set([6, 44, 74]);

    for (let index = 0; index < Math.min(percentiles.length, sizes.length); index++) {
      const percentile = percentiles[index];
      const size = sizes[index];
      if (supportedPercentiles.has(percentile)) {
        sizeByPercentile.set(percentile, size);
      }
      if (supportedDiameters.has(size)) {
        cumulativeByDiameter.set(size, percentile);
      }
    }

    return {
      valueForPercentile10: sizeByPercentile.get(10) ?? null,
      valueForPercentile50: sizeByPercentile.get(50) ?? null,
      valueForPercentile90: sizeByPercentile.get(90) ?? null,
      valueForSize6: cumulativeByDiameter.get(6) ?? null,
      valueForSize44: cumulativeByDiameter.get(44) ?? null,
      valueForSize74: cumulativeByDiameter.get(74) ?? null
    };
  }

  private numericValues(values: Map<string, string[]>, name: string): number[] {
    return (values.get(this.normalize(name)) ?? [])
      .map(value => Number(value))
      .filter((value): value is number => Number.isFinite(value));
  }

  private staticAgingComputedValues(
    values: Map<string, string[]>,
    design: DesignIterationDto
  ): StaticAgingComputedValues {
    const mudVolume = this.numberValue(values, ['mudvolume']);
    const freeFluid = this.numberValue(values, ['freefluid']);
    const columnMass = this.numberValue(values, ['columnmass']) ?? 0;
    const shelfMass = this.numberValue(values, ['shelfmass']) ?? 0;
    const weightsMass = this.numberValue(values, ['weightsmass']) ?? 0;
    const penetration = this.numberValue(values, ['penetration']);
    const totalMass = columnMass + shelfMass + weightsMass;
    const initialSpecificGravity = this.densitySpecificGravityByTitle(design, ['Density'])
      ?? this.staticAgingSpecificGravityFromValues(values, ['initialdensity', 'initialspecificgravity']);
    const topSpecificGravity = this.densitySpecificGravityByTitle(design, ['Top Density'])
      ?? this.staticAgingSpecificGravityFromValues(values, ['topdensity', 'topspecificgravity']);
    const bottomSpecificGravity = this.densitySpecificGravityByTitle(design, ['Bottom Density'])
      ?? this.staticAgingSpecificGravityFromValues(values, ['bottomdensity', 'bottomspecificgravity']);
    const initialDensity = initialSpecificGravity !== null ? initialSpecificGravity * 8.345 : null;
    const topDensity = topSpecificGravity !== null ? topSpecificGravity * 8.345 : null;
    const bottomDensity = bottomSpecificGravity !== null ? bottomSpecificGravity * 8.345 : null;

    return {
      freeFluidVolumePercent: mudVolume !== null && mudVolume > 0
        && freeFluid !== null && freeFluid > 0
        ? (freeFluid / mudVolume) * 100
        : null,
      shearStrength: penetration !== null && penetration > 0
        && totalMass > 0 && initialSpecificGravity !== null && initialSpecificGravity > 0
        ? (3.615 * totalMass / penetration) - 0.256 * initialSpecificGravity
        : null,
      sagFactor: topDensity !== null && bottomDensity !== null
        && topDensity + bottomDensity > 0
        ? bottomDensity / (topDensity + bottomDensity)
        : null,
      sagIndex: initialDensity !== null && initialDensity > 0
        && bottomDensity !== null && bottomDensity > 0
        ? bottomDensity / (2 * initialDensity)
        : null
    };
  }

  private staticAgingDensityPpg(value: number | null): number | null {
    if (value === null || value <= 0) return null;
    return value > 3 ? value : value * 8.345;
  }

  private staticAgingSpecificGravityFromValues(
    values: Map<string, string[]>,
    names: string[]
  ): number | null {
    const densityPpg = this.staticAgingDensityPpg(this.numberValue(values, names));
    return densityPpg !== null ? densityPpg / 8.345 : null;
  }

  private densitySpecificGravityByTitle(
    design: DesignIterationDto,
    titles: string[]
  ): number | null {
    const acceptedTitles = new Set(titles.map(title => this.normalize(title)));
    const densityTests = design.testGroups
      .flatMap(group => group.testInputs)
      .filter(test => this.isDensityTest(test)
        && acceptedTitles.has(this.normalize(test.title ?? '')));

    for (const densityTest of densityTests) {
      const values = this.testValues(densityTest);
      const specificGravity = this.numberValue(values, [
        'specificgravity',
        'densitygml',
        'densitygrampermilliliter',
        'measuredspecificgravity'
      ]);
      if (specificGravity !== null && specificGravity > 0) return specificGravity;
    }

    return null;
  }

  private salinityComputedValues(values: Map<string, string[]>): SalinityComputedValues {
    const mudUsedVolume = this.firstPositiveNumber(values, [
      'mudusedvolume',
      'wholemudsamplevolume',
      'mudsamplevolume'
    ]) ?? 1;
    const alkalineDemand = this.numberValue(values, ['alkalinedemand', 'alkalinedemandvolume']) ?? 0;
    const acidVolume = this.firstPositiveNumber(values, ['acidvolume', 'wholemudacidvolume']) ?? 0;
    const silverNitrateVolume = this.firstPositiveNumber(values, [
      'agno3volume',
      'silvernitratevolume',
      'wholemudsilvernitratevolume'
    ]) ?? 0;
    const edtaVolume = this.firstPositiveNumber(values, [
      'edtavolume',
      'wholemudedtavolume'
    ]) ?? 0;
    const silverNitrateConcentration = this.firstPositiveNumber(values, [
      'wholemudsilvernitrateconcentration',
      'agno3concentration',
      'silvernitrateconcentration'
    ]) ?? 10000;
    const edtaConcentration = this.firstPositiveNumber(values, [
      'edtaconcentration',
      'wholemudedtaconcentration'
    ]) ?? 4000;
    const titration = calculateNafChemistryTitration({
      mudUsedVolume,
      alkalineDemand,
      acidVolume,
      silverNitrateVolume,
      edtaVolume,
      silverNitrateConcentration,
      edtaConcentration
    });

    return {
      nafMudAlkalinity: titration?.nafMudAlkalinity ?? 0,
      wholeMudChloridesVolume: titration?.wholeMudChlorides ?? null,
      wholeMudCalciumVolume: titration?.wholeMudCalcium ?? null,
      limeContent: titration?.limeContent ?? 0
    };
  }

  private firstPositiveNumber(values: Map<string, string[]>, names: string[]): number | null {
    for (const name of names) {
      const value = this.numberValue(values, [name]);
      if (value !== null && value > 0) return value;
    }

    return null;
  }

  private wbmTitrationComputedValues(
    values: Map<string, string[]>,
    test: DesignTestInputDto,
    design: DesignIterationDto
  ): WbmTitrationComputedValues {
    const mudSpecificGravity = this.wbmTitrationMudSpecificGravity(values, test, design);
    const mudSampleWeight = this.numberValue(values, [
      'mudSampleWeight',
      'wholeMudSampleWeight',
      'sampleWeight'
    ]) ?? 0;
    const mudSampleVolumeFromInput = this.numberValue(values, [
      'mudSampleVolume',
      'wbmTitration_mudSampleVolume',
      'wholeMudSampleVolume'
    ]) ?? 0;
    const mudSampleVolume = mudSpecificGravity !== null && mudSpecificGravity > 0 && mudSampleWeight > 0
      ? mudSampleWeight / mudSpecificGravity
      : mudSampleVolumeFromInput;
    const diluentVolume = this.firstPositiveNumber(values, [
      'diluentTitratedVolume',
      'volumeOfDiluentTitratedMl'
    ]) ?? 0;
    const flaskVolume = this.firstPositiveNumber(values, [
      'flaskVolume',
      'volumeOfFlaskMl'
    ]) ?? 0;
    const silverNitrateVolume = this.numberValue(values, [
      'agNo3Volume',
      'wholeMudSilverNitrateVolume',
      'silverNitrateVolume'
    ]) ?? 0;
    const silverNitrateConcentration = this.firstPositiveNumber(values, [
      'agNo3Concentration',
      'wholeMudSilverNitrateConcentration',
      'silverNitrateConcentration'
    ]) ?? 0;
    const bariteLbPerBbl = this.firstPositiveNumber(values, [
      'bariteLbPerBbl',
      'bariteSharePoundsPerBarrel'
    ]) ?? 0;
    const bariteDensity = this.firstPositiveNumber(values, [
      'wbmTitration_bariteDensity',
      'bariteDensity'
    ]) ?? 4.2;
    const chloridesInBlend = diluentVolume > 0 && silverNitrateConcentration > 0
      ? (silverNitrateVolume * silverNitrateConcentration) / diluentVolume
      : Number.NaN;
    const bariteShareGg = mudSpecificGravity !== null && mudSpecificGravity > 0
      ? bariteLbPerBbl / (1000 * mudSpecificGravity)
      : Number.NaN;
    const bariteInFlaskWeight = mudSampleWeight > 0 && Number.isFinite(bariteShareGg)
      ? mudSampleWeight * bariteShareGg
      : Number.NaN;
    const bariteInFlaskVolume = Number.isFinite(bariteInFlaskWeight) && bariteInFlaskWeight >= 0
      ? bariteInFlaskWeight / bariteDensity
      : Number.NaN;
    const volumeMinusBarite = flaskVolume > 0 && Number.isFinite(bariteInFlaskVolume)
      ? flaskVolume - bariteInFlaskVolume
      : Number.NaN;
    const dilutionRatio = Number.isFinite(volumeMinusBarite) && mudSampleVolume > 0
      ? volumeMinusBarite / mudSampleVolume
      : Number.NaN;
    const chloridesCorrection = Number.isFinite(chloridesInBlend) && chloridesInBlend > 0
      ? ((chloridesInBlend * 2) - ((25 + diluentVolume + silverNitrateVolume) * 0.005))
        / (chloridesInBlend * 2)
      : Number.NaN;
    const totalChlorides = Number.isFinite(dilutionRatio)
      && Number.isFinite(chloridesInBlend)
      && Number.isFinite(chloridesCorrection)
      ? dilutionRatio * chloridesInBlend * chloridesCorrection
      : null;

    return { totalChlorides };
  }

  private wbmTitrationMudSpecificGravity(
    values: Map<string, string[]>,
    test: DesignTestInputDto,
    design: DesignIterationDto
  ): number | null {
    const directSpecificGravity = this.firstPositiveNumber(values, [
      'wbmTitration_mudSpecificGravity',
      'measuredSpecificGravity',
      'specificGravity',
      'mudSpecificGravity'
    ]);
    if (directSpecificGravity !== null) return directSpecificGravity;

    for (const candidate of design.testGroups.flatMap(group => group.testInputs)) {
      if (candidate.id === test.id) continue;

      const candidateValues = this.testValues(candidate);
      if (this.isDensityTest(candidate)) {
        const densitySpecificGravity = this.firstPositiveNumber(candidateValues, [
          'measuredSpecificGravity',
          'specificGravity',
          'densitygml',
          'densitygrampermilliliter'
        ]);
        if (densitySpecificGravity !== null) return densitySpecificGravity;
        continue;
      }

      if (!this.isType(
        this.normalize(candidate.testTypeAlias || candidate.testTypeName || candidate.title || ''),
        'pycnometer'
      )) {
        continue;
      }

      const pycnometerSpecificGravity = this.pycnometerAverageSg(candidateValues);
      if (pycnometerSpecificGravity !== null && pycnometerSpecificGravity > 0) {
        return pycnometerSpecificGravity;
      }
    }

    return null;
  }

  private wbmChemistryComputedValues(values: Map<string, string[]>): WbmChemistryComputedValues {
    const inputNames = [
      'wholeMudSampleVolume',
      'mudSampleVolume',
      'filtrateSampleVolume',
      'wholeMudAcidVolume',
      'filtrateAcidPfVolume',
      'filtrateAcidMfVolume',
      'wholeMudSilverNitrateVolume',
      'filtrateSilverNitrateVolume',
      'wholeMudEdtaVolume',
      'filtrateEdtaTotalHardnessVolume',
      'filtrateEdtaCalciumVolume',
      'wholeMudSilverNitrateConcentration',
      'filtrateSilverNitrateConcentration',
      'wholeMudEdtaConcentration',
      'filtrateEdtaConcentration',
      'agNo3Concentration',
      'edtaConcentration'
    ];
    if (!inputNames.some(name => values.has(this.normalize(name)))) {
      return {
        wholeMudAlkalinity: null,
        filtrateAlkalinityPf: null,
        filtrateAlkalinityMf: null,
        filtrateChlorideContent: null,
        filtrateTotalHardness: null,
        filtrateCalciumContent: null,
        filtrateMagnesiumContent: null,
        wholeMudChlorideContent: null,
        wholeMudCalciumContent: null
      };
    }

    const wholeMudSampleVolume = this.positiveNumberOrDefault(
      values,
      ['wholeMudSampleVolume', 'mudSampleVolume'],
      1
    );
    const filtrateSampleVolume = this.numberValue(values, ['filtrateSampleVolume']) ?? 1;
    const wholeMudAcidVolume = this.numberValue(values, ['wholeMudAcidVolume']) ?? 0;
    const filtrateAcidPfVolume = this.numberValue(values, ['filtrateAcidPfVolume']) ?? 0;
    const filtrateAcidMfVolume = this.numberValue(values, ['filtrateAcidMfVolume']) ?? 0;
    const wholeMudSilverNitrateVolume = this.numberValue(values, [
      'wholeMudSilverNitrateVolume',
      'agNo3Volume'
    ]) ?? 0;
    const filtrateSilverNitrateVolume = this.numberValue(values, ['filtrateSilverNitrateVolume']) ?? 0;
    const wholeMudEdtaVolume = this.numberValue(values, ['wholeMudEdtaVolume', 'edtaVolume']) ?? 0;
    const filtrateEdtaTotalHardnessVolume = this.numberValue(values, ['filtrateEdtaTotalHardnessVolume']) ?? 0;
    const filtrateEdtaCalciumVolume = this.numberValue(values, ['filtrateEdtaCalciumVolume']) ?? 0;
    const wholeMudSilverNitrateConcentration = this.positiveNumberOrDefault(
      values,
      ['wholeMudSilverNitrateConcentration', 'agNo3Concentration'],
      10000
    );
    const filtrateSilverNitrateConcentration = this.positiveNumberOrDefault(
      values,
      ['filtrateSilverNitrateConcentration', 'wholeMudSilverNitrateConcentration'],
      10000
    );
    const wholeMudEdtaConcentration = this.positiveNumberOrDefault(
      values,
      ['wholeMudEdtaConcentration', 'edtaConcentration'],
      400
    );
    const filtrateEdtaConcentration = this.positiveNumberOrDefault(
      values,
      ['filtrateEdtaConcentration', 'wholeMudEdtaConcentration'],
      400
    );

    const wholeMudAlkalinity = wholeMudSampleVolume > 0
      ? wholeMudAcidVolume / wholeMudSampleVolume
      : null;
    const hasFiltrateAlkalinity = this.hasValue(values, 'filtrateAcidPfVolume')
      || this.hasValue(values, 'filtrateAcidMfVolume');
    const filtrateChlorideContent = filtrateSampleVolume > 0 && filtrateSilverNitrateConcentration > 0
      ? (filtrateSilverNitrateVolume * filtrateSilverNitrateConcentration) / filtrateSampleVolume
      : null;
    const filtrateTotalHardness = filtrateSampleVolume > 0 && filtrateEdtaConcentration > 0
      ? (filtrateEdtaTotalHardnessVolume * filtrateEdtaConcentration) / filtrateSampleVolume
      : null;
    const filtrateCalciumContent = filtrateSampleVolume > 0 && filtrateEdtaConcentration > 0
      ? (filtrateEdtaCalciumVolume * filtrateEdtaConcentration) / filtrateSampleVolume
      : null;
    const filtrateMagnesiumContent = filtrateTotalHardness !== null && filtrateCalciumContent !== null
      ? Math.max(0, 0.6 * (filtrateTotalHardness - filtrateCalciumContent))
      : null;
    const wholeMudChlorideContent = wholeMudSampleVolume > 0 && wholeMudSilverNitrateConcentration > 0
      ? (wholeMudSilverNitrateVolume * wholeMudSilverNitrateConcentration) / wholeMudSampleVolume
      : null;
    const wholeMudCalciumContent = wholeMudSampleVolume > 0 && wholeMudEdtaConcentration > 0
      ? (wholeMudEdtaVolume * wholeMudEdtaConcentration) / wholeMudSampleVolume
      : null;

    return {
      wholeMudAlkalinity,
      filtrateAlkalinityPf: hasFiltrateAlkalinity ? filtrateAcidPfVolume : null,
      filtrateAlkalinityMf: hasFiltrateAlkalinity ? filtrateAcidMfVolume : null,
      filtrateChlorideContent,
      filtrateTotalHardness,
      filtrateCalciumContent,
      filtrateMagnesiumContent,
      wholeMudChlorideContent,
      wholeMudCalciumContent
    };
  }

  private positiveNumberOrDefault(
    values: Map<string, string[]>,
    names: string[],
    defaultValue: number
  ): number {
    const positiveValue = this.firstPositiveNumber(values, names);
    if (positiveValue !== null) return positiveValue;

    return names.some(name => this.hasValue(values, name)) ? 0 : defaultValue;
  }

  private hasValue(values: Map<string, string[]>, name: string): boolean {
    return values.get(this.normalize(name))?.some(value => value.trim() !== '') ?? false;
  }

  private electricalStabilityAverage(
    values: Map<string, string[]>,
    temperature: number,
    test: DesignTestInputDto
  ): number | null {
    const readingIndices = Array.from(values.keys())
      .map(key => /^esreading(\d+)temperature$/i.exec(key)?.[1])
      .filter((index): index is string => index !== undefined)
      .map(Number)
      .filter(Number.isFinite)
      .sort((left, right) => left - right);

    for (const index of readingIndices) {
      const readingTemperature = this.numberValue(values, [`esreading${index}temperature`]);
      if (readingTemperature !== temperature) continue;

      const voltages = [1, 2, 3]
        .map(voltageIndex => this.numberValue(values, [`esreading${index}voltage${voltageIndex}`]))
        .filter((value): value is number => value !== null && value >= 0);
      if (voltages.length === 0) continue;

      const average = voltages.reduce((sum, value) => sum + value, 0) / voltages.length;
      return Number.isFinite(average) ? Math.trunc(average) : null;
    }

    const inputReadings = this.parsedElectricalStabilityInputReadings(test.parsedInputValues);
    const outputReadings = this.parsedElectricalStabilityOutputReadings(test.testOutputs[0]?.parsedValues ?? []);
    const outputById = new Map<string, ElectricalStabilityParsedReading>();
    for (const reading of outputReadings) {
      if (reading.id) outputById.set(reading.id.toLowerCase(), reading);
    }

    for (const [index, inputReading] of inputReadings.entries()) {
      if (inputReading.temperature !== temperature) continue;

      const outputReading = (inputReading.id
        ? outputById.get(inputReading.id.toLowerCase())
        : undefined) ?? outputReadings[index];
      const average = this.averageElectricalStabilityVoltages(outputReading?.voltages ?? []);
      if (average !== null) return average;
    }

    const legacyTemperature = this.numberValue(values, ['requestedtemperature']);
    if (legacyTemperature !== temperature) return null;

    return this.averageElectricalStabilityVoltages(
      ['voltage1', 'voltage2', 'voltage3']
        .map(key => this.numberValue(values, [key]))
        .filter((value): value is number => value !== null)
    );
  }

  private parsedElectricalStabilityInputReadings(fields: ContentFieldDto[]): ElectricalStabilityParsedReading[] {
    const ids = fields
      .filter(field => this.normalize(field.label) === 'id')
      .map(field => field.value.trim());
    const temperatures = fields
      .filter(field => this.normalize(field.label) === 'temperature')
      .map(field => Number(field.value))
      .map(value => Number.isFinite(value) ? value : null);
    const count = Math.max(ids.length, temperatures.length);

    return Array.from({ length: count }, (_, index) => ({
      id: ids[index] || null,
      temperature: temperatures[index] ?? null,
      voltages: [] as number[]
    }));
  }

  private parsedElectricalStabilityOutputReadings(fields: ContentFieldDto[]): ElectricalStabilityParsedReading[] {
    const readings: ElectricalStabilityParsedReading[] = [];
    let current: ElectricalStabilityParsedReading | null = null;

    for (const field of fields) {
      const label = this.normalize(field.label);
      if (label === 'id') {
        current = { id: field.value.trim() || null, temperature: null, voltages: [] };
        readings.push(current);
        continue;
      }

      if (!this.isElectricalStabilityVoltageLabel(label)) continue;
      if (!current || current.voltages.length >= 3) {
        current = { id: null, temperature: null, voltages: [] };
        readings.push(current);
      }

      const value = Number(field.value);
      if (Number.isFinite(value)) current.voltages.push(value);
    }

    return readings;
  }

  private isElectricalStabilityVoltageLabel(label: string): boolean {
    return label === 'int'
      || label === 'int32'
      || label === 'voltage'
      || /^voltage[123]$/.test(label)
      || label === 'dielectricbreakdownvoltage';
  }

  private averageElectricalStabilityVoltages(voltages: number[]): number | null {
    const validVoltages = voltages.filter(value => Number.isFinite(value) && value >= 0);
    if (validVoltages.length === 0) return null;

    const average = validVoltages.reduce((sum, value) => sum + value, 0) / validVoltages.length;
    return Number.isFinite(average) ? Math.trunc(average) : null;
  }

  private xmlReadings(content: string | null, acceptedNames: string[]): Array<Map<string, string[]>> {
    if (!content) return [];
    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return [];
    const names = new Set(acceptedNames);

    return Array.from(document.getElementsByTagName('*'))
      .filter(element => names.has(element.localName.toLowerCase()))
      .map(element => {
        const values = new Map<string, string[]>();
        for (const child of Array.from(element.children)) {
          const value = child.textContent?.trim();
          if (value) this.addValue(values, child.localName, value);
        }
        return values;
      });
  }

  private hasRheologyValues(row: Record<string, unknown>): boolean {
    const output = row['output'] as Record<string, unknown>;
    return row['temperature'] !== null
      || row['pressure'] !== null
      || Object.values(output).some(value => value !== null);
  }

  private subtract(left: number | null, right: number | null): number | null {
    return left !== null && right !== null ? left - right : null;
  }

  private add(left: number | null, right: number | null): number | null {
    return left !== null && right !== null ? left + right : null;
  }

  private percent(numerator: number | null, denominator: number | null): number | null {
    return numerator !== null && denominator !== null && denominator > 0
      ? 100 * numerator / denominator
      : null;
  }
}